import {
  Component,
  OnDestroy,
  OnInit,
  Inject,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { Subject, Observable } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';
import { Router, NavigationEnd, NavigationStart, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConnectionService } from 'ng-connection-service';
import { ToastrService } from 'ngx-toastr';
import { Title } from '@angular/platform-browser';
import $ from 'jquery';
import { SwUpdate } from '@angular/service-worker';
import { SocketService } from '@_services/socket.service';
import { FuseSidebarService } from './Shared/sidebar/sidebar.service';
import { BehaviorService } from '@_services/Behavior.service';
import { AuthenticationService } from '@_services/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { LicenceAgreementComponent } from './main/licence-agreement/licence-agreement.component';
import { FuseConfigService } from '@_services/config.service';
import { FuseNavigationService } from './layout/components/fuse-navigation/navigation/navigation.service';
import { navigation } from './navigation/navigation';
import { DIALOG_WIDTH } from './_constant/dialog-width.constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  fuseConfig: any;
  navigation: any;
  deviceInfo: any = null;
  isConnected: boolean = true;
  isUser: any = JSON.parse(localStorage.getItem('currentUser') || 'null');
  status: string = 'ONLINE';

  isHelpDeskOpen = signal(false);
  isBulkInvoicingWorkItemOpen = signal(false);
  isFirmDirectoryContactDetailOpen = signal(false);
  isSetupGuideOpen = signal(false);
  isLoggedInUser = signal(false);

  private _jsonURL = 'assets/version.json';
  private _unsubscribeAll$: Subject<void> = new Subject<void>();
  /** Closes open sockets when the page is being hidden or discarded. */
  private closeSocketsOnLeave = () => this.socketService.closeAllSockets();

  /**
   * Whether the authenticated shell should stay hidden for standalone routes.
   *
   * The browser URL is checked before the router is ready so those pages do not flash the main layout.
   *
   * @returns `true` when the current route is a standalone page.
   */
  get isLayoutHidden(): boolean {
    const hash = window.location.hash || '';
    const path = window.location.pathname || '';
    const isStandalonePage =
      hash.includes('privacy-policy') ||
      hash.includes('end-user-licence-agreement') ||
      hash.includes('server-error') ||
      hash.includes('admin-data/complete_signup') ||
      path.includes('privacy-policy') ||
      path.includes('end-user-licence-agreement') ||
      path.includes('server-error') ||
      path.includes('admin-data/complete_signup');

    if (isStandalonePage) {
      return true;
    }

    let child = this.activatedRoute.firstChild;
    while (child) {
      if (child.firstChild) {
        child = child.firstChild;
      } else {
        const routePath = child.snapshot.routeConfig?.path;
        return (
          routePath === 'server-error' ||
          routePath === 'end-user-licence-agreement' ||
          routePath === 'privacy-policy' ||
          routePath === 'admin-data/complete_signup' ||
          routePath === '**'
        );
      }
    }
    return false;
  }

  /**
   * Wires startup behavior that must run before the first change detection.
   *
   * Restores tab payloads, checks for an app update, syncs login across tabs, applies the saved theme, and registers navigation.
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private _fuseConfigService: FuseConfigService,
    private _fuseNavigationService: FuseNavigationService,
    private _fuseSidebarService: FuseSidebarService,
    private _platform: Platform,
    public dialog: MatDialog,
    private router: Router,
    private _authenticationService: AuthenticationService,
    private _behaviorService: BehaviorService,
    private http: HttpClient,
    private _snackBar: MatSnackBar,
    private connectionService: ConnectionService,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private swUpdate: SwUpdate,
    private socketService: SocketService,
  ) {
    this.initializeNewTabData();
    this.checkforVersionUpdate();

    if (
      (!this.swUpdate.isEnabled ||
        (typeof window !== 'undefined' && window.location.hostname.includes('localhost'))) &&
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator
    ) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister().then((success) => {
            if (success) {
              window.location.reload();
            }
          });
        }
      });
    }

    window.addEventListener('storage', (event) => {
      if (event.key === 'session_login' && event.newValue) {
        localStorage.removeItem('session_login');
        const hash = window.location.hash || '';
        const query = hash.includes('?')
          ? hash.substring(hash.indexOf('?') + 1)
          : (window.location.search || '').replace(/^\?/, '');
        const returnUrl = new URLSearchParams(query).get('returnUrl');
        if (returnUrl) {
          window.location.hash = returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`;
        }
        window.location.reload();
      }
    });

    setInterval(() => this.checkforVersionUpdate(), 60 * 1000);

    this._behaviorService.currentUser$.pipe(takeUntil(this._unsubscribeAll$)).subscribe(() => {
      this.isUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    });

    let tabData: any;
    let windowNameId: any;
    window.addEventListener('storage', (event) => {
      if (event.storageArea != localStorage) {
        return;
      }

      if (event.key === 'setBackup' && event.newValue) {
        const windowId = window.name.split('_');
        windowNameId = windowId && windowId[1] ? windowId[1] : undefined;
        tabData = windowNameId ? localStorage.getItem(windowNameId) : null;
      }

      if (event.key === 'silq_theme_reload' && event.newValue) {
        window.location.reload();
        return;
      }

      if (event.key === 'theme_type') {
        return;
      }

      if (event.key === 'currentUser' && event.newValue && event.oldValue) {
        try {
          const previousTheme = JSON.parse(event.oldValue)?.THEMECOLOUR;
          const nextTheme = JSON.parse(event.newValue)?.THEMECOLOUR;
          if (previousTheme !== nextTheme) {
            window.location.reload();
            return;
          }
        } catch {}
      }

      if (event.key && event.key !== 'silq_logout_event' && localStorage.getItem('session_token')) {
        this._behaviorService.setTimerCountData();
      }

      if (event.key === 'silq_logout_event' && event.newValue) {
        this._authenticationService.tabLogout();
        if (tabData && windowNameId) {
          localStorage.setItem(windowNameId, tabData);
        }
      }

      if (!localStorage.getItem('session_token') || !localStorage.getItem('currentUser')) {
        this._authenticationService.tabLogout();
        if (tabData && windowNameId) {
          localStorage.setItem(windowNameId, tabData);
        }
      }
    });

    let currentUser: any = this._authenticationService.currentUserValue;
    if (currentUser) {
      this._fuseConfigService.config = {
        colorTheme: currentUser.THEMECOLOUR,
        layout: {
          sidepanel: { hidden: currentUser.SIDEPANELHIDE },
          navbar: {
            hidden: currentUser.NAVBARHIDE,
            folded: currentUser.NAVBARFOLDED,
          },
        },
      };
      this._behaviorService.setToolBaarSetting({
        NAVBARFOLDED: currentUser.NAVBARFOLDED,
        NAVBARHIDE: currentUser.NAVBARHIDE,
        OPENINNEWTABWINDOWS: currentUser.OPENINNEWTABWINDOWS,
      });

      this.connectionService
        .monitor()
        .pipe(takeUntil(this._unsubscribeAll$))
        .subscribe((currentState) => {
          const hasNetworkConnection = currentState.hasNetworkConnection;
          this.isConnected = hasNetworkConnection;
          if (currentState.hasNetworkConnection) {
            if (this.status == 'OFFLINE') {
              this.toastr.success('Back Online');
            }
            this.status = 'ONLINE';
          } else {
            this.status = 'OFFLINE';
          }
        });
    } else {
      this._fuseConfigService.config = {
        colorTheme: 'theme-yellow-light',
      };
    }

    const appcuesWindow = window as any;
    this._authenticationService.currentUser
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((user: any) => {
        if (!user || !appcuesWindow.AppcuesReady) {
          return;
        }
        appcuesWindow.AppcuesReady(() => {
          const appcuesId = user.APPCUESDATA?.APPCUESUSERID || user.UserGuid;
          if (!appcuesId || !appcuesWindow.Appcues) {
            return;
          }
          const appcuesProps = user.APPCUESDATA || {
            createdAt: new Date().getTime(),
            purchasedAd: new Date().getTime(),
            planTier: 'Standard',
            role: user?.PRODUCTTYPE,
            accountId: user?.UserId,
            firstName: user?.UserName,
          };
          appcuesWindow.Appcues.identify(appcuesId, appcuesProps);
        });
      });

    let lastAppcuesUrl = '';
    router.events.pipe(takeUntil(this._unsubscribeAll$)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        const nextUrl = event.url.split('?')[0];
        if (lastAppcuesUrl && nextUrl !== lastAppcuesUrl) {
          appcuesWindow.AppcuesReady(() => {
            appcuesWindow.Appcues.clearShow?.();
          });
        }
      }
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects.split('?')[0];
        if (url === lastAppcuesUrl) {
          return;
        }
        lastAppcuesUrl = url;
        appcuesWindow.AppcuesReady(() => {
          appcuesWindow.Appcues.page();
        });
      }
    });

    this.navigation = navigation;

    this._fuseNavigationService.register('main', this.navigation);

    this._fuseNavigationService.setCurrentNavigation('main');

    if (this._platform.ANDROID || this._platform.IOS) {
      this.document.body.classList.add('is-mobile');
    }

    this.epicFunction();

    this._behaviorService.applicationVersion$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((result) => {
        const apppVersion = localStorage.getItem('applicationVersion');
        if (
          apppVersion &&
          apppVersion !== null &&
          apppVersion !== 'null' &&
          apppVersion != undefined &&
          apppVersion != 'undefined' &&
          apppVersion != ''
        ) {
          if (apppVersion < result) {
            this.getJSON().subscribe((versionData) => {
              let snackBarRef = this._snackBar.open(versionData.message, 'Refresh', {
                duration: 200000000,
              });
              snackBarRef.onAction().subscribe(() => {
                localStorage.setItem('applicationVersion', versionData.version);
                window.location.reload();
              });
            });
          }
        } else {
          localStorage.setItem('applicationVersion', result);
        }
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else if (child.snapshot.data && child.snapshot.data['title']) {
              return child.snapshot.data['title'];
            } else {
              return null;
            }
          }
          return null;
        }),
        takeUntil(this._unsubscribeAll$),
      )
      .subscribe((data: any) => {
        if (data) {
          if (
            data != 'Deliverables' &&
            data != 'Estimate' &&
            data != 'Matter-Appointments' &&
            data != 'Receipts-Credits' &&
            data != 'Matter-Invoices' &&
            data != 'Matter-Trust' &&
            data != 'Work-In-Progress'
          ) {
            this.titleService.setTitle(data);
          }
        }
      });
  }

  /** Starts session, theme, and sidebar subscriptions after the component is initialized. */
  ngOnInit(): void {
    let token = localStorage.getItem('session_token');
    if (!token || token == '' || token == null || token == 'null') {
      const fontLink = this.document.getElementById('google-font');
      if (!fontLink) {
        this._authenticationService.addGoogleFont();
      }
    }

    if (!!token && token !== 'null') {
      this.removeGoogleFont();
    }

    window.addEventListener('pagehide', this.closeSocketsOnLeave);
    this.handleWebSocket();
    localStorage.setItem('isstart', 'no');
    this._authenticationService.openWSConnection(localStorage.getItem('session_token'));

    // Subscribe to config changes
    this._fuseConfigService.config
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((config: any) => {
        this.fuseConfig = config;
        if (this.fuseConfig?.layout?.width === 'boxed') {
          this.document.body.classList.add('boxed');
        } else {
          this.document.body.classList.remove('boxed');
        }

        this.applyBodyColorTheme();
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll$),
      )
      .subscribe((event: any) => {
        this.applyBodyColorTheme();
        if (
          event.url == '/bank-feed/bank-feed-dashboard#connect' &&
          this.activatedRoute.snapshot.fragment === 'connect'
        ) {
          $('#checkbankfeed').click();
        }
      });

    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    this.isLoggedInUser.set(!!currentUser?.UserGuid);

    this._authenticationService.currentUserSubject$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((result: any) => {
        if (result) {
          this.isLoggedInUser.set(!!(result?.UserGuid || result?.USERGUID));
          this.document.documentElement.dataset['productType'] = result.PRODUCTTYPE || '';
          this._fuseConfigService.config = {
            colorTheme: result.THEMECOLOUR || 'theme-default',
            layout: {
              sidepanel: { hidden: result.SIDEPANELHIDE },
              navbar: {
                hidden: result.NAVBARHIDE,
                folded: result.NAVBARFOLDED,
              },
            },
          };
          this._behaviorService.setToolBaarSetting({
            NAVBARFOLDED: result.NAVBARFOLDED,
            NAVBARHIDE: result.NAVBARHIDE,
            OPENINNEWTABWINDOWS: result.OPENINNEWTABWINDOWS,
          });
        } else {
          this.isLoggedInUser.set(false);
          delete this.document.documentElement.dataset['productType'];
          this._fuseConfigService.config = {
            colorTheme: 'theme-yellow-light',
          };
        }
      });

    // Subscribe to Help Desk sidebar state
    this._behaviorService.isHelpDeskOpen$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((isOpen) => {
        this.isHelpDeskOpen.set(isOpen);
      });

    this._behaviorService.isBulkInvoicingWorkItemOpen$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((isOpen) => {
        this.isBulkInvoicingWorkItemOpen.set(isOpen);
      });

    this._behaviorService.isFirmDirectoryContactDetailOpen$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((isOpen) => {
        this.isFirmDirectoryContactDetailOpen.set(isOpen);
      });

    this._behaviorService.isSetupGuideOpen$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((isOpen) => {
        this.isSetupGuideOpen.set(isOpen);
      });
  }

  /** Closes sockets and completes component subscriptions. */
  ngOnDestroy(): void {
    window.removeEventListener('pagehide', this.closeSocketsOnLeave);
    this.socketService.closeAllSockets();
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  /**
   * Copies a new-tab payload from session storage into the matter or client selection.
   *
   * Reads `TAB_PAYLOAD_<window.name>` and removes it after a successful copy.
   */
  initializeNewTabData() {
    try {
      if (!window.name) return;

      const payloadKey = 'TAB_PAYLOAD_' + window.name;
      const payload = sessionStorage.getItem(payloadKey);

      if (!payload) return;

      const decoded = JSON.parse(payload);

      if (decoded.modul?.includes('MATTERDETAILS')) {
        const materIDofTab = window.name.split('_');
        const windowNameId = materIDofTab && materIDofTab[1] ? materIDofTab[1] : undefined;
        localStorage.setItem(windowNameId, JSON.stringify(decoded.data));
        sessionStorage.setItem('selectedMatter', JSON.stringify(decoded.data));
      }

      if (decoded.modul?.includes('CLIENTDETAILS')) {
        const materIDofTab = window.name.split('_');
        const windowNameId = materIDofTab && materIDofTab[1] ? materIDofTab[1] : undefined;
        localStorage.setItem(windowNameId, JSON.stringify(decoded.data));
        sessionStorage.setItem('selectedClient', JSON.stringify(decoded.data));
      }

      sessionStorage.removeItem(payloadKey);
    } catch (e) {
      console.log('No valid tab payload');
    }
  }

  /**
   * Loads the version message shown when a newer application version is available.
   *
   * @returns The `assets/version.json` response.
   */
  public getJSON(): Observable<any> {
    return this.http.get(this._jsonURL);
  }

  /** Removes the Poppins stylesheet added for logged-out pages. */
  removeGoogleFont() {
    const fontLink = this.document.getElementById('google-font');
    if (fontLink) {
      fontLink.remove();
    }
  }

  /**
   * Detects a phone or tablet from the browser user agent.
   *
   * @returns `true` when the user agent matches a known mobile device.
   */
  isMobileDevice(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  }

  /** Opens the licence agreement dialog on a narrow mobile viewport. */
  epicFunction() {
    const isMobile = this.isMobileDevice();
    if (window.innerWidth < 1024) {
      if (isMobile) {
        const dialogRef = this.dialog.open(LicenceAgreementComponent, {
          disableClose: true,
          width: DIALOG_WIDTH.W850,
          data: { action: 'MD' },
        });
        dialogRef.afterClosed().subscribe((result) => {});
      }
    }
  }

  /**
   * Opens the matter and snapshot sockets when a session token is present.
   *
   * @returns A promise that resolves after both sockets are opened, or immediately when no token exists.
   */
  async handleWebSocket() {
    let token = localStorage.getItem('session_token');
    if (token) {
      await this.socketService.openSocketMatter(token);
      await this.socketService.openSocketSnapshoteneration();
    }
  }

  /**
   * Asks the service worker for an update and prompts the user when one is ready.
   *
   * @returns A promise that resolves after the update check finishes.
   */
  async checkforVersionUpdate() {
    if (this.swUpdate.isEnabled) {
      const isNewVersion = await this.swUpdate.checkForUpdate();
      if (isNewVersion) {
        this.promptUser();
      }
    }
  }

  /** Shows a snack bar that reloads the page when the user chooses Refresh. */
  promptUser(): void {
    let snackBarRef = this._snackBar.open('A new version of the app is available.', 'Refresh', {
      duration: 200000000,
    });

    snackBarRef.onAction().subscribe(() => {
      window.location.reload();
    });
  }

  /**
   * Toggles a tracked sidebar, or a Fuse sidebar when `key` is not one of the tracked panels.
   *
   * @param key - Sidebar identifier. Tracked keys are `themeOptionsPanelHelp`, `BulkInvoicingWorkItemPanel`, `firmDirectoryContactDetail`, and `SetUpGuideOpen`.
   */
  toggleSidebarOpen(key: string): void {
    if (key === 'themeOptionsPanelHelp') {
      this._behaviorService.isHelpDeskOpen$.next(!this.isHelpDeskOpen());
    } else if (key === 'BulkInvoicingWorkItemPanel') {
      this._behaviorService.isBulkInvoicingWorkItemOpen$.next(!this.isBulkInvoicingWorkItemOpen());
    } else if (key === 'firmDirectoryContactDetail') {
      this._behaviorService.isFirmDirectoryContactDetailOpen$.next(
        !this.isFirmDirectoryContactDetailOpen(),
      );
    } else if (key === 'SetUpGuideOpen') {
      this._behaviorService.isSetupGuideOpen$.next(!this.isSetupGuideOpen());
    } else {
      this._fuseSidebarService.getSidebar(key).toggleOpen();
      this._fuseSidebarService.sidebarMenuPopup.next(true);
    }
  }

  /**
   * Whether the active URL is the complete-signup route.
   *
   * @returns `true` when the hash, path, or router URL includes `admin-data/complete_signup`.
   */
  private isCompleteSignupRoute(): boolean {
    const hash = window.location.hash || '';
    const path = window.location.pathname || '';
    const url = this.router?.url || '';
    return (
      hash.includes('admin-data/complete_signup') ||
      path.includes('admin-data/complete_signup') ||
      url.includes('admin-data/complete_signup')
    );
  }

  /**
   * Replaces the body theme class with the active color theme.
   *
   * Complete signup always uses Solicitor Light. Other routes use `fuseConfig.colorTheme`.
   */
  private applyBodyColorTheme(): void {
    for (let i = 0; i < this.document.body.classList.length; i++) {
      const className = this.document.body.classList[i];
      if (className.startsWith('theme-')) {
        this.document.body.classList.remove(className);
      }
    }

    const colorTheme = this.isCompleteSignupRoute()
      ? 'theme-yellow-light'
      : this.fuseConfig?.colorTheme;
    if (colorTheme) {
      this.document.body.classList.add(colorTheme);
    }
  }
}
